import { type UserEntity } from '@solinkcorp/api-types';
import { type LocaleDefinition } from '@solinkcorp/locale';
import UserActions from 'actions/UserActions';
import { CallhomeApi } from 'clients';
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import { getFeatureStandardValidUserType } from 'src/hooks/useUserFeatures';
import { AuthStore } from 'stores/AuthStore';
import UserStore from 'stores/UserStore';
import FeaturesUtil from 'utils/FeaturesUtil';

export function getUserTypeString(user: UserEntity, locale: LocaleDefinition) {
  if (user) {
    const isSolinkUser = FeaturesUtil.isSolinkUser(user);
    const validUserType = getFeatureStandardValidUserType(user);
    return `${locale.usersPage[validUserType]}${
      isSolinkUser ? ' & ' + locale.usersPage.solink : ''
    }`;
  }
  return '';
}

export function getUserInfoString(user: UserEntity, locale: LocaleDefinition) {
  return `${user?.name || locale.general.unknown}(${user?.email}) - ${getUserTypeString(
    user,
    locale,
  )}`;
}

// A user can't edit the userType of itself
// nor edit the userType of users that with the same userType.
// Standard users shouldn't do this either.
export function canEditUserType(user: UserEntity) {
  const authUser = AuthStore.getUser();
  const authUserId = AuthService.getSession()?.userId;

  return (
    authUser &&
    authUserId !== user.id &&
    FeaturesUtil.isAdminUser() &&
    authUser.userType !== user.userType
  );
}

export async function getLocationGroups() {
  const customerId = AuthService.getSession()?.tenantId;
  if (customerId) {
    const locationGroups = await CallhomeApi().getLocationGroups({ customerId });
    return locationGroups;
  }
  console.error('Error when getting location groups: customerId is undefined.');
  return [];
}

export const enableDigestIfDisabled = () => {
  const user = UserStore.getCurrentUser();
  if (!user) {
    console.error('Error when enabling digest: user is undefined.');
    return;
  }

  if (!user.isDigestEnabled) {
    UserActions.updateUser({ userId: user.id, data: { isDigestEnabled: true } });
    AnalyticsLogger.toggleDigestNotification({ isEnabled: true });
  }
};
